/**
 * useAiServiceStatus — polls the singleton public.ai_service_status row
 * so the app shell can render a banner when the global Anthropic
 * circuit breaker is OPEN.
 *
 * Polling cadence is 60s — the breaker tracks consecutive failures
 * across all Edge Functions and only flips OPEN after the threshold,
 * so a 60s read is more than fast enough to surface a real outage.
 * Single-row read; cost is negligible.
 *
 * Fail-open semantics — when the read errors (RLS, transient network,
 * etc.) we return null and the banner stays hidden. Observability reads
 * must never block the UI.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'

export type AiServiceState = 'CLOSED' | 'OPEN' | 'HALF_OPEN'

export interface AiServiceStatusRow {
  state:                AiServiceState
  opened_at:            string | null
  consecutive_failures: number
  last_failure_message: string | null
  last_success_at:      string | null
  cooldown_seconds:     number
}

function narrowRow(raw: unknown): AiServiceStatusRow | null {
  if (raw === null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const state = r.state
  if (state !== 'CLOSED' && state !== 'OPEN' && state !== 'HALF_OPEN') return null
  return {
    state,
    opened_at:            typeof r.opened_at            === 'string' ? r.opened_at            : null,
    consecutive_failures: typeof r.consecutive_failures === 'number' ? r.consecutive_failures : 0,
    last_failure_message: typeof r.last_failure_message === 'string' ? r.last_failure_message : null,
    last_success_at:      typeof r.last_success_at      === 'string' ? r.last_success_at      : null,
    cooldown_seconds:     typeof r.cooldown_seconds     === 'number' ? r.cooldown_seconds     : 300,
  }
}

export function useAiServiceStatus(): UseQueryResult<AiServiceStatusRow | null> {
  const supabase = useSupabase()

  return useQuery({
    queryKey: ['ai-service-status'],
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<AiServiceStatusRow | null> => {
      const { data, error } = await supabase
        .from('ai_service_status')
        .select('state, opened_at, consecutive_failures, last_failure_message, last_success_at, cooldown_seconds')
        .eq('id', 'global')
        .maybeSingle()
      if (error) return null
      return narrowRow(data)
    },
  })
}
